import { zodResolver } from '@hookform/resolvers/zod'
import { AwardType, TenantMemberOrTeamDisplayData } from '@kudos/http-client'
import { createQualitiesSchema } from '@shared/forms/QualitiesAutoCompleteField'
import { createRichTextAreaSchema } from '@shared/forms/richTextEditorField/createRichTextAreaSchema'
import useQualities from '@shared/hooks/useQualities'
import { TFunction } from '@shared/types/types'
import stripHtml from '@shared/utils/stripHtml'
import { useT } from '@transifex/react'
import { isEmpty } from 'lodash/fp'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'
import useSendAwardMutations from './useSendAwardMutations'

const createSendAwardFormSchema = (t: TFunction, qualitiesRequired: boolean) =>
  z
    .object({
      amount: z.preprocess(
        value => (typeof value === 'string' ? parseInt(value, 10) : value),
        z
          .number({
            message: t('Points required'),
          })
          .min(0, { message: t('Points must be a positive number') }),
      ),
      message: createRichTextAreaSchema(t),
      sendTo: z
        .array(z.custom<TenantMemberOrTeamDisplayData>())
        .min(1, { message: t('Select a recipient') }),
      refinementId: z.string().optional(),
      tenantWide: z.boolean(),
    })
    .and(createQualitiesSchema(t, qualitiesRequired))

export type SendAwardFormData = z.infer<ReturnType<typeof createSendAwardFormSchema>>

const useSendAwardForm = (awardType: AwardType) => {
  const t = useT()
  const { id: awardTypeId, points } = awardType
  const { qualities } = useQualities(awardType.qualitiesEnabled, awardType.availableValueIds)
  const SendAwardFormSchema = createSendAwardFormSchema(
    t,
    awardType.qualitiesEnabled && !isEmpty(qualities),
  )

  const defaults: SendAwardFormData = {
    amount: points,
    message: '',
    sendTo: [],
    qualities: [],
    refinementId: undefined,
    tenantWide: true,
  }

  const formMethods = useForm<SendAwardFormData>({
    resolver: zodResolver(SendAwardFormSchema),
    defaultValues: defaults,
  })

  const { reset, setError, formState, control } = formMethods
  const { publishAward } = useSendAwardMutations(awardTypeId, setError)
  const sendTo = useWatch({ control, name: 'sendTo' })
  const message = useWatch({ control, name: 'message' })
  const isFormDirty = formState.isDirty && (sendTo.length > 0 || stripHtml(message).length > 0)

  const handleResetAwardForm = () => {
    reset(defaults)
  }

  return {
    formMethods,
    publishAward,
    handleResetAwardForm,
    isFormDirty,
    qualities,
  }
}

export default useSendAwardForm
